import { RadixTooltip } from '@timelyapp/react';
import type { AgentSpeed, AssignProjectBody } from 'api/agentSessions/agentSessions.api';
import classNames from 'classnames';
import ProjectBadge from 'components/projects/ProjectBadge';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getProjectById } from 'redux/selectors/projects';
import { extractProjectBase } from 'redux/selectors/utils/projects';
import styles from './AgentTrackingSessions.module.scss';
import {
  type AgentPeriod,
  describeModelUsage,
  describeRecordedSpeed,
  describeServiceTier,
  describeUnknownSpeed,
  formatCost,
  formatDuration,
  formatTokens,
  hasAgentMetric,
  modelUsage,
  serviceTierUsage,
  summaryPendingEndsAt,
  summaryStatus,
  totalAgentTokens,
} from './agentTracking.model';

/** Shared period presentation for the expanded table and complete session history. */
export default function AgentWorkPeriod({
  period,
  marker,
  project,
  links,
  inheritedProject = false,
  showDates = false,
  showSeconds = true,
  showTokens = true,
  showCost = true,
  compact = false,
  onAssign,
}: {
  period: AgentPeriod;
  marker?: React.ReactNode;
  project: React.ReactNode;
  links?: React.ReactNode;
  inheritedProject?: boolean;
  showDates?: boolean;
  showSeconds?: boolean;
  showTokens?: boolean;
  showCost?: boolean;
  compact?: boolean;
  onAssign?: (sessionId: number, body: AssignProjectBody) => Promise<unknown>;
}) {
  const tier = serviceTierUsage([period]);
  return (
    <li
      className={classNames(styles.activityBlock, { [styles.dialogPeriod]: compact })}
      data-live-updated={period.liveChange ? '' : undefined}
    >
      <label className={styles.activityMarker}>
        <span aria-hidden="true" />
        {marker}
      </label>
      <div className={styles.activityContent}>
        <div className={styles.activityTime}>
          {showDates && (
            <>
              <span>{period.date}</span>
              <span aria-hidden="true">·</span>
            </>
          )}
          {period.provider === 'claude_cowork' ? (
            <span>Activity summary</span>
          ) : (
            <time dateTime={period.startedAt}>
              {period.start}–{period.end}
            </time>
          )}
        </div>
        <WorkSummary period={period} />
        <div className={styles.activityMeta}>
          <span className={styles.activityModel}>
            {modelUsage([period])
              .map(
                usage =>
                  `${usage.label}${usage.requests ? ` · ${formatCount(usage.requests, 'request')}` : ''}`
              )
              .join(' · ')}
            {tier && (
              <>
                {' · '}
                <span title={describeServiceTier(tier)}>
                  {tier.tier === 'priority'
                    ? 'Fast mode'
                    : tier.tier === 'ultrafast'
                      ? 'Ultrafast mode'
                      : `${tier.tier.charAt(0).toUpperCase() + tier.tier.slice(1)} processing`}
                </span>
              </>
            )}
          </span>
          <div
            className={classNames(styles.activityProject, {
              [styles.inheritedProject]: inheritedProject,
            })}
          >
            <span className={styles.sameProjectLabel} aria-hidden="true">
              {period.projectId === null ? 'Unassigned' : 'Same project'}
            </span>
            <div className={styles.periodProjectControl}>{project}</div>
          </div>
          {links && <div className={styles.activityLinks}>{links}</div>}
        </div>
        <details className={styles.usageDetails}>
          <summary>Usage details</summary>
          <ModelList periods={[period]} detailed onAssign={onAssign} />
          <div className={styles.activityUsage}>
            <span>
              {hasAgentMetric(period, 'input_tokens') ? formatTokens(period.inputTokens) : '—'}{' '}
              input
            </span>
            <span>
              {hasAgentMetric(period, 'output_tokens') ? formatTokens(period.outputTokens) : '—'}{' '}
              output
            </span>
            <span>
              {hasAgentMetric(period, 'cache_read_tokens') &&
              hasAgentMetric(period, 'cache_write_tokens')
                ? formatTokens(period.cacheReadTokens + period.cacheWriteTokens)
                : '—'}{' '}
              cached
            </span>
          </div>
        </details>
      </div>
      {showSeconds && (
        <div
          className={styles.periodMetric}
          data-label="Agent time"
          style={{ '--metric-column': 'seconds' } as React.CSSProperties}
        >
          <MetricValue
            unavailable={!hasAgentMetric(period, 'seconds')}
            label="Agent time"
            reason="Agent time was not reported. Excluded from totals."
          >
            {formatDuration(period.activeSeconds)}
          </MetricValue>
        </div>
      )}
      {showTokens && (
        <div
          className={styles.periodMetric}
          data-label="Tokens"
          style={{ '--metric-column': 'tokens' } as React.CSSProperties}
        >
          <MetricValue
            unavailable={!hasAgentMetric(period, 'tokens')}
            label="Token usage"
            reason="Token usage was not reported. Excluded from totals."
          >
            {formatTokens(totalAgentTokens(period))}
          </MetricValue>
        </div>
      )}
      {showCost && (
        <div
          className={styles.periodMetric}
          data-label="Est. cost"
          style={{ '--metric-column': 'cost' } as React.CSSProperties}
        >
          <MetricValue
            unavailable={!hasAgentMetric(period, 'cost')}
            label="Estimated cost"
            reason="A cost estimate is not available. Excluded from cost totals."
          >
            {formatCost(period.cost)}
          </MetricValue>
        </div>
      )}
    </li>
  );
}

/**
 * Which models did the work, biggest token share first. `detailed` adds how
 * each model was called (requests, fast-mode count, effort) when the provider
 * reported it; the compact form lists names only, so the session row stays short.
 */
function ModelList({
  periods,
  detailed = false,
  onAssign,
}: {
  periods: AgentPeriod[];
  detailed?: boolean;
  onAssign?: (sessionId: number, body: AssignProjectBody) => Promise<unknown>;
}) {
  const models = modelUsage(periods);
  if (!models.length) return null;
  return (
    <span
      className={classNames(styles.models, { [styles.modelsDetailed]: detailed })}
      aria-label="Models used"
    >
      {models.map(usage => {
        const period = periods[0];
        const missing = period.speedUnknownUsage?.[usage.model];
        const missingTokens = ['input', 'output', 'cache_read', 'cache_write'].reduce(
          (sum, key) => sum + (missing?.[key] ?? 0),
          0
        );
        const editable =
          periods.length === 1 && period.provider === 'codex' && missingTokens > 0 && !!onAssign;
        const detail = detailed
          ? describeModelUsage(
              usage,
              describeRecordedSpeed(usage, missingTokens, period.speedOverrides?.[usage.model])
            )
          : '';
        const control = editable ? (
          <SpeedControl
            period={period}
            model={usage.model}
            label={usage.label}
            onAssign={onAssign!}
          />
        ) : null;
        return (
          <span key={usage.model} className={styles.model} title={usage.model}>
            {usage.label}
            {detailed && <> {formatTokens(usage.tokens)}</>}
            {detail && <span className={styles.modelDetail}>{detail}</span>}
            {detailed && missingTokens > 0 && (
              <span className={styles.modelDetail}>
                <span title={`${missingTokens.toLocaleString()} tokens have no reported speed`}>
                  Unknown speed: {describeUnknownSpeed(missingTokens, usage.tokens)}
                </span>
                {' · Priced as '}
                {control ??
                  `${period.speedOverrides?.[usage.model] === 'ultrafast' ? 'Ultrafast mode' : period.speedOverrides?.[usage.model] === 'priority' ? 'Fast mode' : 'Standard'} (assumed)`}
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

function SpeedControl({
  period,
  model,
  label,
  onAssign,
}: {
  period: AgentPeriod;
  model: string;
  label: string;
  onAssign: (sessionId: number, body: AssignProjectBody) => Promise<unknown>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const override = period.speedOverrides?.[model];
  return (
    <>
      <select
        className={styles.speedSelect}
        aria-label={`Speed for ${label}`}
        title="Only applies to tokens whose speed was not reported. Recorded speeds stay unchanged. Updates the estimate, not ChatGPT itself."
        value={override ?? 'standard'}
        disabled={busy}
        onChange={async event => {
          const value = event.target.value;
          setBusy(true);
          setError(false);
          try {
            await onAssign(period.sessionId, {
              segment_ids: [period.segmentId],
              speed_override: { model, tier: value === 'standard' ? null : (value as AgentSpeed) },
            });
          } catch {
            setError(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        <option value="standard">Standard</option>
        <option value="priority">Fast mode</option>
        <option value="ultrafast">Ultrafast mode</option>
      </select>
      <span title="Speed was not reported for this usage"> (assumed)</span>
      {error && <span role="alert"> Could not save speed. Try again.</span>}
    </>
  );
}

/** Long imported summaries should not make the activity table difficult to scan. */
function WorkSummary({ period }: { period: AgentPeriod }) {
  const [expanded, setExpanded] = useState(false);
  const { summary } = period;
  const textRef = useRef<HTMLParagraphElement>(null);
  const [canExpand, setCanExpand] = useState(false);
  const status = summaryStatus(period);
  // Nothing re-renders the row when the wait runs out, so wake up then and show "No summary".
  const [, wake] = useState(0);
  const pendingEndsAt = summaryPendingEndsAt(period);
  useEffect(() => {
    if (pendingEndsAt === null) return;
    const timer = setTimeout(() => wake(value => value + 1), pendingEndsAt - Date.now() + 50);
    return () => clearTimeout(timer);
  }, [pendingEndsAt]);
  useLayoutEffect(() => {
    setExpanded(false);
    const text = textRef.current;
    if (!text || !summary?.trim() || status !== 'ready') {
      setCanExpand(false);
      return;
    }
    const measure = () => {
      // Measure the collapsed preview even while expanded, then restore its state.
      const wasClamped = text.classList.contains(styles.summaryClamped);
      text.classList.add(styles.summaryClamped);
      const overflows = text.scrollHeight > text.clientHeight;
      if (!wasClamped) text.classList.remove(styles.summaryClamped);
      setCanExpand(overflows);
      if (!overflows) setExpanded(false);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(text);
    return () => observer.disconnect();
  }, [summary, status]);
  if (status === 'pending')
    return (
      <span className={classNames(styles.secondary, styles.pendingSummary)}>
        Summary will be generated once the agent stops working and the period ends (usually about 15
        minutes later).
      </span>
    );
  if (!summary?.trim()) return <span className={styles.secondary}>No summary</span>;
  return (
    <div className={styles.workSummary}>
      <p
        ref={textRef}
        className={classNames(styles.summaryText, {
          [styles.summaryClamped]: !expanded,
          [styles.summaryArrived]: period.liveChange?.kind === 'summary',
        })}
      >
        {summary}
      </p>
      {canExpand && (
        <button
          type="button"
          className={styles.summaryToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

function formatCount(count: number, label: string) {
  return `${count} ${label}${count === 1 ? '' : 's'}`;
}

export function ProjectLabel({
  projectId,
  fallbackName,
  fallbackColor,
  quietWhenEmpty = false,
  emptyTooltip,
}: {
  projectId: number | null;
  fallbackName?: string;
  /** With fallbackName: the color of a project the viewer has not loaded (a colleague's). */
  fallbackColor?: string | null;
  /** Keep an empty label out of the way until its row is hovered or focused, like the empty picker. */
  quietWhenEmpty?: boolean;
  /** Said on hover of an empty label: why it is a label and not a picker. */
  emptyTooltip?: string;
}) {
  const projects = useSelector(getProjectById);
  const variant = projectId === null ? undefined : projects[projectId];
  const project = variant ? extractProjectBase(variant) : undefined;
  const name = project?.name ?? fallbackName ?? 'Unassigned';
  const color = project?.color || project?.client?.color || (project ? undefined : fallbackColor);
  const empty = projectId === null;
  const explained = empty && !!emptyTooltip;
  const label = (
    <span
      className={styles.projectLabel}
      title={explained ? undefined : name}
      data-assigned={!empty}
      data-empty-assignment={quietWhenEmpty && empty ? true : undefined}
      style={
        color
          ? ({ '--agent-project-color': `#${color.replace(/^#/, '')}` } as React.CSSProperties)
          : undefined
      }
    >
      {color && (
        <ProjectBadge color={`#${color.replace(/^#/, '')}`} size="small" aria-hidden="true" />
      )}
      <span className={styles.projectName}>{name}</span>
    </span>
  );
  if (!explained) return label;
  return (
    <RadixTooltip tooltip={emptyTooltip} delayDuration={0}>
      {label}
    </RadixTooltip>
  );
}

/** A visible placeholder distinguishes missing measurements from measured zero. */
export function MetricValue({
  unavailable,
  label,
  reason,
  children,
}: {
  unavailable: boolean;
  label: string;
  reason: string;
  children: React.ReactNode;
}) {
  if (!unavailable) return <>{children}</>;
  return (
    <RadixTooltip tooltip={<span className={styles.metricTooltip}>{reason}</span>} side="top">
      <span
        className={classNames(styles.identityTrigger, styles.unavailableMetric)}
        tabIndex={0}
        aria-label={`${label} unavailable`}
      >
        —
      </span>
    </RadixTooltip>
  );
}
